import { NavLink, useLocation } from "react-router-dom";
import { useAdminLayout } from "../../layouts/AdminLayout";
import { Logo } from "../Logo";

/**
 * Navigation item configuration
 */
interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
}

/**
 * Navigation section with divider
 */
interface NavSection {
  title?: string;
  items: NavItem[];
}

/**
 * AdminSidebar - Dark sidebar navigation for admin panel.
 *
 * Features:
 * - Collapsible on desktop (icon-only mode)
 * - Slide-out drawer on mobile
 * - Active state highlighting
 * - Section dividers
 * - JumpBean branding
 */
export function AdminSidebar() {
  const { sidebarCollapsed, setSidebarCollapsed, mobileMenuOpen, setMobileMenuOpen } =
    useAdminLayout();
  const location = useLocation();

  const navSections: NavSection[] = [
    {
      items: [
        {
          to: "/admin",
          label: "Dashboard",
          icon: <DashboardIcon />,
          end: true,
        },
        {
          to: "/admin/users",
          label: "Users",
          icon: <UsersIcon />,
        },
        {
          to: "/admin/analytics",
          label: "Analytics",
          icon: <AnalyticsIcon />,
        },
        {
          to: "/admin/emails",
          label: "Emails",
          icon: <EmailIcon />,
        },
      ],
    },
    {
      title: "Content",
      items: [
        {
          to: "/admin/challenges",
          label: "Challenges",
          icon: <ChallengesIcon />,
        },
        {
          to: "/admin/skills",
          label: "Skills",
          icon: <SkillsIcon />,
        },
        {
          to: "/admin/ai-history",
          label: "AI History",
          icon: <AIHistoryIcon />,
        },
      ],
    },
    {
      title: "System",
      items: [
        {
          to: "/admin/settings",
          label: "Settings",
          icon: <SettingsIcon />,
        },
      ],
    },
  ];

  const sidebarClasses = `
    fixed lg:static inset-y-0 left-0 z-40
    flex flex-col
    bg-gray-900 text-white
    transition-all duration-300 ease-in-out
    ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
    ${sidebarCollapsed ? "lg:w-20" : "lg:w-64"}
    w-64
  `;

  return (
    <aside className={sidebarClasses} aria-label="Admin navigation">
      {/* Header with logo and collapse button */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-800">
        <NavLink
          to="/admin"
          className={`flex items-center gap-3 ${sidebarCollapsed ? "lg:justify-center lg:w-full" : ""}`}
        >
          <div className="w-8 h-8 flex-shrink-0">
            <BeanIcon />
          </div>
          {!sidebarCollapsed && (
            <span className="font-display font-bold text-lg text-white whitespace-nowrap">
              JumpBean <span className="text-sunshine-400">Admin</span>
            </span>
          )}
        </NavLink>

        {/* Mobile close button */}
        <button
          onClick={() => setMobileMenuOpen(false)}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-800 transition-colors"
          aria-label="Close menu"
        >
          <CloseIcon />
        </button>

        {/* Desktop collapse toggle */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className={`hidden lg:flex p-2 rounded-lg hover:bg-gray-800 transition-colors ${
            sidebarCollapsed ? "absolute right-2" : ""
          }`}
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <CollapseIcon collapsed={sidebarCollapsed} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {navSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-6">
            {section.title && !sidebarCollapsed && (
              <div className="px-4 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {section.title}
                </span>
              </div>
            )}
            {section.title && sidebarCollapsed && (
              <div className="mx-4 mb-2 border-t border-gray-800" />
            )}
            <ul className="space-y-1 px-3">
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) => `
                      flex items-center gap-3 px-3 py-2.5 rounded-lg
                      transition-all duration-200
                      group relative
                      ${sidebarCollapsed ? "lg:justify-center" : ""}
                      ${
                        isActive
                          ? "bg-gray-800 text-white"
                          : "text-gray-400 hover:text-white hover:bg-gray-800/50"
                      }
                    `}
                  >
                    <span className="w-5 h-5 flex-shrink-0">{item.icon}</span>
                    {!sidebarCollapsed && (
                      <span className="font-medium text-sm">{item.label}</span>
                    )}

                    {/* Tooltip for collapsed state */}
                    {sidebarCollapsed && (
                      <div
                        className="
                          absolute left-full ml-2 px-3 py-1.5
                          bg-gray-800 text-white text-sm font-medium
                          rounded-lg shadow-lg
                          opacity-0 invisible group-hover:opacity-100 group-hover:visible
                          transition-all duration-200
                          whitespace-nowrap z-50
                          hidden lg:block
                        "
                      >
                        {item.label}
                        <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-800" />
                      </div>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer - back to app link */}
      <div className="p-3 border-t border-gray-800">
        <NavLink
          to="/play"
          className={`
            flex items-center gap-3 px-3 py-2.5 rounded-lg
            text-gray-400 hover:text-white hover:bg-gray-800/50
            transition-all duration-200
            ${sidebarCollapsed ? "lg:justify-center" : ""}
          `}
        >
          <span className="w-5 h-5 flex-shrink-0">
            <BackToAppIcon />
          </span>
          {!sidebarCollapsed && (
            <span className="font-medium text-sm">Back to App</span>
          )}
        </NavLink>
      </div>
    </aside>
  );
}

// Icons
function BeanIcon() {
  return (
    <svg viewBox="0 0 32 32" fill="none" className="w-full h-full">
      <ellipse
        cx="16"
        cy="16"
        rx="12"
        ry="14"
        fill="#FFD93D"
        transform="rotate(-15 16 16)"
      />
      <ellipse
        cx="16"
        cy="16"
        rx="10"
        ry="12"
        fill="#FFEB85"
        transform="rotate(-15 16 16)"
      />
      <circle cx="12" cy="12" r="2" fill="#333" />
      <circle cx="20" cy="12" r="2" fill="#333" />
      <path
        d="M11 18 Q16 22 21 18"
        stroke="#333"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function DashboardIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
    </svg>
  );
}

function AnalyticsIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
    </svg>
  );
}

function ChallengesIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path
        fillRule="evenodd"
        d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function SkillsIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
    </svg>
  );
}

function AIHistoryIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path
        fillRule="evenodd"
        d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function CollapseIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className={`w-5 h-5 transition-transform duration-300 ${
        collapsed ? "rotate-180" : ""
      }`}
    >
      <path
        fillRule="evenodd"
        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function BackToAppIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path
        fillRule="evenodd"
        d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export default AdminSidebar;
